import { Component } from "@theme/component";

// Debug logging flag - set to false to disable all debug logs
const DEBUG_FABRIC_FILTERS = false;

function debugLog(method, message, data = {}) {
  if (!DEBUG_FABRIC_FILTERS) return;
  const now = new Date();
  const timestamp = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}.${now.getMilliseconds().toString().padStart(3, "0")}`;
  console.log(`[${timestamp}][FabricFilters::${method}]`, message, data);
}

/**
 * Fabric Filters Component
 * Handles the fabric filters expansion state across morphs
 */
class FabricFiltersComponent extends Component {
  /**
   * Gets the details wrapper element
   */
  get detailsElement() {
    return this.querySelector("details[data-filter-wrapper]");
  }

  /**
   * Gets the mobile filter drawer element.
   * The drawer lives OUTSIDE this component in the DOM (as a sibling dialog-component),
   * so we find it via document.querySelector by its known ID.
   */
  get mobileDrawer() {
    return document.querySelector("#fab-filters-drawer");
  }

  /**
   * Finds the size picker component in either the desktop layout or the mobile drawer.
   */
  get sizePicker() {
    const desktop = this.querySelector("size-picker-component");
    if (desktop) return desktop;
    const drawer = this.mobileDrawer;
    return drawer ? drawer.querySelector("size-picker-component") : null;
  }

  connectedCallback() {
    debugLog("connectedCallback", "Component connected");
    this.restoreExpandedState();
    this.attachEventListeners();
    this.attachChipListeners();

    // Set up viewport change listener to close mobile drawer on desktop
    this.setupViewportChangeListener();

    // Initialize size-related functionality if a size picker exists
    // in either the desktop layout (inside this component) or the mobile drawer (outside it)
    if (this.sizePicker) {
      debugLog(
        "connectedCallback",
        "Size picker found, initializing size-related features",
      );
      this.updateSizeChip();
      this.listenForSizeChanges();
      this.listenForExternalFilterClears();
    } else {
      debugLog(
        "connectedCallback",
        "Size picker not found, skipping size-related features",
      );
    }

    // Handle bfcache (Back-Forward Cache) restoration.
    // When a user navigates back (e.g., product page → collection page),
    // the browser serves the page from bfcache — connectedCallback does NOT
    // re-fire. localStorage may have been updated on the product page,
    // so we need to re-read it and update the size chip + pricing.
    if (!this._pageshowBound) {
      this._pageshowHandler = (event) => {
        if (event.persisted) {
          debugLog(
            "pageshow",
            "Page restored from bfcache, refreshing size state",
          );
          // Defer to run AFTER the browser's own bfcache form restoration.
          // Browsers may reset <select> values to their frozen state after pageshow,
          // so we need to re-apply localStorage values after that completes.
          setTimeout(() => {
            const picker = this.sizePicker;
            if (
              picker &&
              typeof picker.restoreStateFromLocalStorage === "function"
            ) {
              picker.restoreStateFromLocalStorage();
              // restoreStateFromLocalStorage() already dispatches fabricSizeChange,
              // which triggers listenForSizeChanges → updateSizeChip.
              // Just update chip visibility after a tick to be safe.
              setTimeout(() => {
                this.updateSizeChip();
                this.updateChipsContainerVisibility();
              }, 50);
            }
          }, 0);
        }
      };
      window.addEventListener("pageshow", this._pageshowHandler);
      this._pageshowBound = true;
    }
  }

  /**
   * Called by morph.js after the component is updated
   * This is crucial for re-attaching event listeners after DOM morphing
   */
  updatedCallback() {
    debugLog("updatedCallback", "Component updated (DOM morphed)", {
      timestamp: Date.now(),
      hasOpenDetails: this.detailsElement?.open,
    });

    // CRITICAL: Call parent updatedCallback to update refs
    super.updatedCallback();

    // Clear FOUC guard styles that the server-rendered HTML re-introduces on every morph.
    // The CSS onload handler only fires once (on initial load), so morphed-in
    // visibility:hidden / opacity:0 would otherwise persist permanently.
    if (this.hasAttribute("data-fab-fouc-hide")) {
      this.style.visibility = "";
      this.style.opacity = "";
    }

    // Disable transitions during morph to prevent jarring animations
    this.classList.add("fab-morphing");

    // Re-attach event listeners (they were lost during morph)
    this.attachEventListeners();
    this._mobileChipListenersAttached = false; // Drawer DOM was morphed, old listeners are gone
    this.attachChipListeners();

    // After morph, Liquid re-renders counts without the size filter.
    // Reset the tracked state so updateSizeFilterCounts re-applies the +1 delta correctly.
    this._sizeFilterCountedAsActive = false;

    // Only handle size picker if it exists (check both desktop + mobile drawer)
    const sizePicker = this.sizePicker;
    if (sizePicker) {
      // Delay size picker restoration and chip update to ensure morph is complete
      // We need to wait for the DOM to fully settle before restoring state
      requestAnimationFrame(() => {
        if (typeof sizePicker.restoreStateFromLocalStorage === "function") {
          debugLog(
            "updatedCallback",
            "Manually triggering size picker restoration after morph",
          );
          sizePicker.restoreStateFromLocalStorage();
        }

        // Wait one more frame for restoration to apply
        requestAnimationFrame(() => {
          this.updateSizeChip();
          this.updateChipsContainerVisibility();
        });
      });
    } else {
      debugLog(
        "updatedCallback",
        "Size picker not found, skipping size-related updates",
      );
      // Still update chips visibility even without size picker
      requestAnimationFrame(() => {
        this.updateChipsContainerVisibility();
      });
    }

    // Restore UI state based on details[open] and active filter
    this.restoreUIAfterMorph();

    // Re-enable transitions after morph completes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.remove("fab-morphing");
        debugLog("updatedCallback", "Morph complete, transitions re-enabled");
      });
    });
  }

  /**
   * Restores the expanded filter state on initial load
   */
  restoreExpandedState() {
    // Auto-expand first filter with active values on initial load
    this.autoExpandActiveFilter();
  }

  /**
   * Restores UI state after morph (details.open is already preserved)
   */
  restoreUIAfterMorph() {
    // Check if we have a stored filter state from before the chip removal
    const storedFilterId = sessionStorage.getItem("fabricActiveFilter");

    if (storedFilterId) {
      // Clear the stored state regardless of whether we restore
      sessionStorage.removeItem("fabricActiveFilter");

      // Only restore the tab if there are still active filter values to display.
      // The server adds --has-active when a category has checked filters;
      // if no category has it, the last filter was just removed and we should collapse.
      const hasActiveFilters = this.querySelector(
        ".fab-category-content--has-active",
      );

      if (hasActiveFilters) {
        const button = this.querySelector(
          `[data-filter-id="${storedFilterId}"]`,
        );
        const content = this.querySelector(
          `[data-filter-content="${storedFilterId}"]`,
        );

        if (button && content) {
          if (this.detailsElement) {
            this.detailsElement.open = true;
          }
          button.classList.add("fab-category-button--active");
          content.classList.add(
            "fab-category-content--active",
            "fab-category-content--visible",
          );
          return;
        }
      }
    }

    if (!this.detailsElement?.open) {
      return;
    }

    // Find which filter content is currently active
    const activeContent = this.querySelector(".fab-category-content--active");

    if (activeContent) {
      const filterId = activeContent.getAttribute("data-filter-content");
      const button = this.querySelector(`[data-filter-id="${filterId}"]`);

      if (button) {
        // Restore the active button state
        button.classList.add("fab-category-button--active");
      }
    } else {
      // No active content found, but details is open - fall back to auto-expanding first active filter
      this.autoExpandActiveFilter();

      // If autoExpandActiveFilter didn't find any active filter to expand,
      // close the details element since there's nothing to show
      if (!this.querySelector(".fab-category-content--active")) {
        this.detailsElement.open = false;
      }
    }
  }

  /**
   * Auto-expands the first filter with active values
   */
  autoExpandActiveFilter() {
    const activeContent = this.querySelector(
      ".fab-category-content--has-active",
    );

    if (!activeContent) {
      return;
    }

    const filterId = activeContent.getAttribute("data-filter-content");
    const button = this.querySelector(`[data-filter-id="${filterId}"]`);

    if (!button) {
      return;
    }

    // Open the details element
    this.detailsElement.open = true;

    // Mark button as active
    button.classList.add("fab-category-button--active");

    // Show the active content
    activeContent.classList.add(
      "fab-category-content--active",
      "fab-category-content--visible",
    );
  }

  /**
   * Attaches event listeners to filter category buttons using event delegation
   */
  attachEventListeners() {
    const buttonsContainer = this.querySelector(".fab-block__categories");
    if (!buttonsContainer) {
      debugLog("attachEventListeners", "No buttons container found");
      return;
    }

    const buttons = this.querySelectorAll(".fab-category-button");
    const contents = this.querySelectorAll(".fab-category-content");

    debugLog(
      "attachEventListeners",
      `Setting up event delegation for ${buttons.length} category buttons`,
    );

    // Remove any existing listener to prevent duplicates
    if (this._categoryClickHandler) {
      buttonsContainer.removeEventListener("click", this._categoryClickHandler);
    }

    // Use event delegation - single listener on the container
    this._categoryClickHandler = (event) => {
      const button = event.target.closest(".fab-category-button");
      if (!button) return;

      const filterId = button.getAttribute("data-filter-id");
      const isCurrentlyActive = button.classList.contains(
        "fab-category-button--active",
      );
      const currentActiveButton = this.querySelector(
        ".fab-category-button--active",
      );

      debugLog(
        "attachEventListeners",
        "Category button clicked via delegation",
        {
          filterId,
          isCurrentlyActive,
          timestamp: Date.now(),
        },
      );

      // If clicking the currently active button, collapse everything
      if (isCurrentlyActive && currentActiveButton === button) {
        // Collapse - close the details element
        this.detailsElement.open = false;
        button.classList.remove("fab-category-button--active");

        // Hide all content
        contents.forEach((content) => {
          content.classList.remove(
            "fab-category-content--active",
            "fab-category-content--visible",
          );
        });
      } else {
        // Expand or switch category
        // Remove active state from all buttons
        buttons.forEach((btn) =>
          btn.classList.remove("fab-category-button--active"),
        );

        // Add active state to clicked button
        button.classList.add("fab-category-button--active");

        // Open the details element (this gets preserved by morph!)
        this.detailsElement.open = true;

        // Find currently active content for fade transition
        const currentActive = this.querySelector(
          ".fab-category-content--active",
        );
        const newActive = this.querySelector(
          `[data-filter-content="${filterId}"]`,
        );

        if (currentActive && currentActive !== newActive) {
          // Fade out current
          currentActive.classList.remove("fab-category-content--visible");
          setTimeout(() => {
            currentActive.classList.remove("fab-category-content--active");
            // Fade in new
            if (newActive) {
              newActive.classList.add("fab-category-content--active");
              setTimeout(() => {
                newActive.classList.add("fab-category-content--visible");
              }, 10);
            }
          }, 200);
        } else if (newActive && !currentActive) {
          // No current active, just show the new one
          newActive.classList.add("fab-category-content--active");
          setTimeout(() => {
            newActive.classList.add("fab-category-content--visible");
          }, 10);
        }
      }
    };

    buttonsContainer.addEventListener("click", this._categoryClickHandler);
    debugLog("attachEventListeners", "Event delegation listener attached");
  }

  /**
   * Attaches event listeners to filter chips using event delegation.
   * Handles both desktop (.fab-active-chips) and mobile drawer (.fab-drawer-chips) containers.
   */
  attachChipListeners() {
    // Shared click handler for chip interactions (works for both desktop & mobile)
    const chipClickHandler = (event) => {
      // Check if clicked element is a remove button or inside one
      const removeButton = event.target.closest(".fab-chip__remove");
      if (removeButton) {
        const chip = removeButton.closest(
          ".fab-chip:not(.fab-chip--clear-all)",
        );
        if (chip) {
          debugLog(
            "attachChipListeners",
            "Chip remove button clicked via delegation",
          );

          // Check if this is the size chip
          if (chip.classList.contains("fab-chip--size")) {
            this.removeSizeFilter();
          } else {
            // Pass the chip's container context so removeFilter finds the correct form
            this.removeFilter(chip);
          }
        }
        return;
      }

      // Check if clicked element is the Clear All button
      const clearAllButton = event.target.closest(".fab-chip--clear-all");
      if (clearAllButton) {
        debugLog(
          "attachChipListeners",
          "Clear All button clicked via delegation",
        );
        this.clearAllFilters(clearAllButton);
      }
    };

    // Desktop chips container
    const desktopChips = this.querySelector(".fab-active-chips");
    if (desktopChips) {
      if (this._chipClickHandler) {
        desktopChips.removeEventListener("click", this._chipClickHandler);
      }
      this._chipClickHandler = chipClickHandler;
      desktopChips.addEventListener("click", chipClickHandler);
      const chipCount = desktopChips.querySelectorAll(
        ".fab-chip:not(.fab-chip--clear-all)",
      ).length;
      debugLog("attachChipListeners", `Desktop chips: ${chipCount} chips`);
    }

    // Mobile drawer chips container (lives outside this component, in the drawer dialog)
    this._attachMobileChipListeners(chipClickHandler);
  }

  /**
   * Attaches click listeners to the mobile drawer chips container.
   * Called from attachChipListeners and lazily from updateSizeChip
   * (since the drawer may not exist when connectedCallback first runs).
   * @param {Function} [handler] - Click handler. If omitted, reuses the desktop handler.
   */
  _attachMobileChipListeners(handler) {
    const chipClickHandler = handler || this._chipClickHandler;
    if (!chipClickHandler) return;

    const drawer = this.mobileDrawer;
    const mobileChips = drawer
      ? drawer.querySelector(".fab-drawer-chips")
      : null;
    if (mobileChips) {
      if (this._mobileChipClickHandler) {
        mobileChips.removeEventListener("click", this._mobileChipClickHandler);
      }
      this._mobileChipClickHandler = chipClickHandler;
      mobileChips.addEventListener("click", chipClickHandler);
      this._mobileChipListenersAttached = true;
      debugLog(
        "_attachMobileChipListeners",
        "Mobile drawer chip listeners attached",
      );
    }
  }

  /**
   * Sets up a listener for viewport changes to close the mobile drawer
   * when the viewport switches to desktop size (750px+)
   */
  setupViewportChangeListener() {
    // Only set up once
    if (this._viewportListenerBound) {
      return;
    }

    const DESKTOP_BREAKPOINT = 750;
    const mediaQuery = window.matchMedia(
      `(min-width: ${DESKTOP_BREAKPOINT}px)`,
    );

    const handleViewportChange = (event) => {
      if (event.matches) {
        // Viewport is now desktop size - close the mobile drawer if it's open
        debugLog(
          "setupViewportChangeListener",
          "Viewport changed to desktop, closing mobile drawer",
        );
        this.closeMobileDrawer();
      }
    };

    // Listen for changes
    mediaQuery.addEventListener("change", handleViewportChange);
    this._viewportListenerBound = true;

    debugLog(
      "setupViewportChangeListener",
      "Viewport change listener attached",
      {
        breakpoint: DESKTOP_BREAKPOINT,
        currentlyDesktop: mediaQuery.matches,
      },
    );

    // Also close drawer immediately if we're already on desktop
    // (handles case where page loads in desktop but drawer was left open from a previous session)
    if (mediaQuery.matches) {
      this.closeMobileDrawer();
    }
  }

  /**
   * Closes the mobile filter drawer
   */
  closeMobileDrawer() {
    // Find the drawer dialog-component by ID
    const drawerComponent = document.querySelector("#fab-filters-drawer");
    if (!drawerComponent) {
      debugLog("closeMobileDrawer", "Drawer component not found");
      return;
    }

    // Find the dialog element inside
    const dialog = drawerComponent.querySelector("dialog");
    if (dialog && dialog.open) {
      debugLog("closeMobileDrawer", "Closing open drawer");
      // Use the dialog's close method
      dialog.close();

      // Also trigger the dialog-component's closeDialog method if available
      if (typeof drawerComponent.closeDialog === "function") {
        drawerComponent.closeDialog();
      }
    }
  }

  /**
   * Removes a single filter
   */
  /**
   * Finds the correct facets-form-component for a given chip element.
   * Mobile drawer chips live inside the dialog-component (outside this component),
   * so we search up to the nearest dialog-component and find the form within it.
   * Desktop chips are inside fabric-filters-component and use the first form found.
   */
  findFormForChip(chip) {
    // Check if chip is inside the mobile drawer
    const dialogComponent = chip.closest("dialog-component");
    if (dialogComponent) {
      return dialogComponent.querySelector("facets-form-component");
    }
    // Desktop fallback
    return this.querySelector("facets-form-component");
  }

  removeFilter(chip) {
    const paramName = chip.getAttribute("data-param-name");
    const paramValue = chip.getAttribute("data-param-value");

    debugLog("removeFilter", "Chip removal initiated", {
      paramName,
      paramValue,
      timestamp: Date.now(),
    });

    // Store the current filter state BEFORE removing
    this.storeFilterState();
    debugLog("removeFilter", "Filter state stored in sessionStorage");

    // Find the correct facets form component based on chip location
    const facetsForm = this.findFormForChip(chip);
    if (!facetsForm) {
      debugLog("removeFilter", "ERROR: facets-form-component not found");
      return;
    }

    // Find the corresponding checkbox and uncheck it
    const checkbox = facetsForm.querySelector(
      `input[name="${paramName}"][value="${paramValue}"]`,
    );
    if (checkbox) {
      debugLog("removeFilter", "Checkbox found, unchecking", {
        wasChecked: checkbox.checked,
        checkboxName: checkbox.name,
        checkboxValue: checkbox.value,
      });

      checkbox.checked = false;

      // Use the proper updateFilters method instead of form.submit()
      debugLog("removeFilter", "Calling facetsForm.updateFilters()");
      facetsForm.updateFilters();
    } else {
      debugLog("removeFilter", "ERROR: Checkbox not found", {
        queriedName: paramName,
        queriedValue: paramValue,
      });
    }
  }

  /**
   * Stores the current active filter tab state
   */
  storeFilterState() {
    const activeButton = this.querySelector(".fab-category-button--active");
    if (activeButton) {
      const filterId = activeButton.getAttribute("data-filter-id");
      sessionStorage.setItem("fabricActiveFilter", filterId);
    }
  }

  /**
   * Clears all active filters
   * @param {HTMLElement} [triggerElement] - The element that triggered the clear (used to find correct form)
   */
  clearAllFilters(triggerElement) {
    debugLog("clearAllFilters", "Clear All initiated", {
      timestamp: Date.now(),
    });

    // Store the current filter state BEFORE clearing to preserve expanded tab
    this.storeFilterState();
    debugLog("clearAllFilters", "Filter state stored in sessionStorage");

    // Only clear size picker if it exists (desktop or mobile drawer)
    const sizePicker = this.sizePicker;
    if (sizePicker) {
      // Clear localStorage for size picker
      localStorage.removeItem("fabricSizePickerState");
      debugLog("clearAllFilters", "Cleared localStorage for size picker");

      // Reset size picker selects to 0
      const sizeSelects = sizePicker.querySelectorAll("select");
      debugLog(
        "clearAllFilters",
        `Found ${sizeSelects.length} size picker selects to reset`,
      );
      sizeSelects.forEach((select) => {
        debugLog(
          "clearAllFilters",
          `Resetting select: ${select.name} from ${select.value} to 0`,
        );
        select.value = "0";
      });

      // NEW: Dispatch fabricSizeFilterCleared event to notify pricing script
      debugLog("clearAllFilters", "Dispatching fabricSizeFilterCleared event");
      const event = new CustomEvent("fabricSizeFilterCleared", {
        detail: { source: "clearAll" },
        bubbles: true,
      });
      document.dispatchEvent(event);
    } else {
      debugLog(
        "clearAllFilters",
        "Size picker not found, skipping size picker clearing",
      );
    }

    // Find the correct facets form component based on trigger location
    const facetsForm = triggerElement
      ? this.findFormForChip(triggerElement)
      : this.querySelector("facets-form-component");
    if (!facetsForm) {
      debugLog("clearAllFilters", "ERROR: facets-form-component not found");
      return;
    }

    // Uncheck all checked filter checkboxes
    const checkboxes = facetsForm.querySelectorAll(
      'input[type="checkbox"]:checked',
    );
    debugLog(
      "clearAllFilters",
      `Found ${checkboxes.length} checked checkboxes to clear`,
    );
    checkboxes.forEach((checkbox) => {
      debugLog(
        "clearAllFilters",
        `Unchecking: ${checkbox.name} = ${checkbox.value}`,
      );
      checkbox.checked = false;
    });

    // Use the proper updateFilters method instead of form.submit()
    debugLog("clearAllFilters", "Calling facetsForm.updateFilters()");
    facetsForm.updateFilters();
  }

  /**
   * Listens for size picker changes
   */
  listenForSizeChanges() {
    debugLog("listenForSizeChanges", "Setting up fabricSizeChange listener");
    document.addEventListener("fabricSizeChange", () => {
      debugLog(
        "listenForSizeChanges",
        "fabricSizeChange event received, calling updateSizeChip",
      );
      this.updateSizeChip();
    });
  }

  /**
   * Listens for external filter clears (from default Horizon filters component)
   * When the default filter's "Clear All" is clicked, it clears product form filters
   * but doesn't know about our custom sizing filter, so we need to clear it manually
   *
   * IMPORTANT: This should ONLY trigger when "Clear All" is clicked, NOT when
   * individual chips are removed one by one (which should preserve custom sizing)
   */
  listenForExternalFilterClears() {
    // Only set up listener if size picker exists (desktop or mobile drawer)
    if (!this.sizePicker) {
      debugLog(
        "listenForExternalFilterClears",
        "Size picker not found, skipping external clear listener",
      );
      return;
    }

    debugLog(
      "listenForExternalFilterClears",
      "Setting up MutationObserver for external filter clears",
    );

    // Watch for changes to the URL (which happens when filters are cleared)
    let previousUrl = window.location.href;
    let hadProductFilters = false;

    // Check initial state
    const initialUrl = new URL(window.location.href);
    const customSizingParams = [
      "width-feet",
      "width-inches",
      "length-feet",
      "length-inches",
      "diameter-feet",
      "diameter-inches",
      "shape-filter",
      "grid",
    ];
    const initialProductFilterParams = Array.from(
      initialUrl.searchParams.keys(),
    ).filter((key) => !customSizingParams.includes(key));
    hadProductFilters = initialProductFilterParams.length > 0;

    const observer = new MutationObserver(() => {
      const currentUrl = window.location.href;

      // Check if URL changed
      if (currentUrl !== previousUrl) {
        debugLog("listenForExternalFilterClears", "URL changed", {
          from: previousUrl,
          to: currentUrl,
        });

        const url = new URL(currentUrl);

        // Get all params except custom sizing params
        const productFilterParams = Array.from(url.searchParams.keys()).filter(
          (key) => !customSizingParams.includes(key),
        );

        debugLog("listenForExternalFilterClears", "Params analysis", {
          allParams: Array.from(url.searchParams.keys()),
          productFilterParams,
          hadProductFilters,
          customSizingParams: Array.from(url.searchParams.keys()).filter(
            (key) => customSizingParams.includes(key),
          ),
        });

        // ONLY clear if:
        // 1. We previously HAD product filters
        // 2. Now we have NO product filters
        // 3. Custom sizing params were also removed from URL (indicating Clear All was clicked)
        const hasCustomSizingParams = Array.from(url.searchParams.keys()).some(
          (key) => customSizingParams.includes(key),
        );

        if (
          hadProductFilters &&
          productFilterParams.length === 0 &&
          !hasCustomSizingParams
        ) {
          debugLog(
            "listenForExternalFilterClears",
            "Clear All detected - all params removed including custom sizing",
          );

          // Clear our custom sizing filter
          localStorage.removeItem("fabricSizePickerState");

          // Reset size picker selects to 0
          const sizeSelects = this.querySelectorAll(
            "size-picker-component select",
          );
          sizeSelects.forEach((select) => {
            select.value = "0";
          });

          // Update the size chip to hide it
          this.updateSizeChip();

          debugLog(
            "listenForExternalFilterClears",
            "Custom sizing filter cleared",
          );
        } else {
          debugLog(
            "listenForExternalFilterClears",
            "Not a Clear All - preserving custom sizing",
          );
        }

        // Update state for next comparison
        hadProductFilters = productFilterParams.length > 0;
        previousUrl = currentUrl;
      }
    });

    // Observe changes to the body (URL changes trigger DOM updates)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Updates the size chip based on size picker values.
   * Syncs both desktop and mobile drawer size chips.
   */
  updateSizeChip() {
    debugLog("updateSizeChip", "Starting updateSizeChip");

    // Lazily attach mobile chip listeners if the drawer now exists but listeners aren't set up.
    // This handles the case where connectedCallback ran before the drawer was in the DOM.
    if (!this._mobileChipListenersAttached) {
      this._attachMobileChipListeners();
    }

    // Find all size chips (desktop + mobile drawer)
    // Desktop chips are inside this component; mobile drawer chips are outside
    const desktopSizeChips = Array.from(
      this.querySelectorAll(".fab-chip--size"),
    );
    const drawer = this.mobileDrawer;
    const mobileSizeChips = drawer
      ? Array.from(drawer.querySelectorAll(".fab-chip--size"))
      : [];
    const sizeChips = [...desktopSizeChips, ...mobileSizeChips];

    debugLog("updateSizeChip", "Elements found", {
      sizeChipCount: sizeChips.length,
    });

    if (sizeChips.length === 0) {
      debugLog("updateSizeChip", "ERROR: No size chips found");
      return;
    }

    // Check both size pickers (desktop + mobile) and use whichever has valid dimensions.
    // They share state via localStorage but the user may be interacting with only one.
    const desktopPicker = this.querySelector("size-picker-component");
    const drawerForPicker = this.mobileDrawer;
    const mobilePicker = drawerForPicker
      ? drawerForPicker.querySelector("size-picker-component")
      : null;

    let dimensions = null;
    if (desktopPicker) dimensions = this.getSizeDimensions(desktopPicker);
    if (!dimensions && mobilePicker)
      dimensions = this.getSizeDimensions(mobilePicker);

    debugLog("updateSizeChip", "Dimensions extracted", {
      dimensions,
      desktopPicker: !!desktopPicker,
      mobilePicker: !!mobilePicker,
    });

    if (!desktopPicker && !mobilePicker) {
      debugLog("updateSizeChip", "ERROR: no size-picker-component found");
      return;
    }

    if (!dimensions) {
      // No valid size selected, hide all size chips
      debugLog("updateSizeChip", "No valid dimensions, hiding chips");
      sizeChips.forEach((chip) => (chip.style.display = "none"));
      this.updateSizeFilterCounts(false);
      this.updateChipsContainerVisibility();
      return;
    }

    // Calculate area to validate minimum size requirement
    const area = this.calculateAreaFromDimensions(dimensions);
    debugLog("updateSizeChip", "Area calculated", { area });

    // Hide chips if area is invalid (less than 12 sq ft or incomplete selection)
    if (area < 12) {
      debugLog(
        "updateSizeChip",
        "Area is less than 12 sq ft or incomplete, hiding chips",
        { area },
      );
      sizeChips.forEach((chip) => (chip.style.display = "none"));
      this.updateSizeFilterCounts(false);
      this.updateChipsContainerVisibility();
      return;
    }

    // Show all size chips with formatted label
    const formattedLabel = this.formatSizeLabel(dimensions);
    debugLog("updateSizeChip", "Formatted label", { formattedLabel });

    sizeChips.forEach((chip) => {
      const label = chip.querySelector("[data-size-label]");
      if (label) {
        label.textContent = formattedLabel;
      }
      chip.style.display = "inline-flex";
    });

    debugLog("updateSizeChip", "All size chips updated", {
      count: sizeChips.length,
    });

    // Update count badges (Size row badge + total counts)
    this.updateSizeFilterCounts(true);

    // Update container visibility to show Clear All button
    this.updateChipsContainerVisibility();
  }

  /**
   * Updates chips container visibility based on whether any chips are visible.
   * Handles both desktop (.fab-active-chips) and mobile drawer (.fab-drawer-chips) containers.
   */
  updateChipsContainerVisibility() {
    // Lazily attach mobile chip listeners if the drawer now exists but listeners aren't set up
    if (!this._mobileChipListenersAttached) {
      this._attachMobileChipListeners();
    }

    // Update desktop chips container
    const desktopContainer = this.querySelector(".fab-active-chips");
    if (desktopContainer) {
      this._updateSingleChipsContainer(desktopContainer);
    }

    // Update mobile drawer chips container (outside this component)
    const drawer = this.mobileDrawer;
    const mobileContainer = drawer
      ? drawer.querySelector(".fab-drawer-chips")
      : null;
    if (mobileContainer) {
      this._updateSingleChipsContainer(mobileContainer);
    }
  }

  /**
   * Updates a single chips container's visibility
   * @param {HTMLElement} container - The chips container to update
   */
  _updateSingleChipsContainer(container) {
    const sizeChip = container.querySelector(".fab-chip--size");
    const regularChips = container.querySelectorAll(
      ".fab-chip:not(.fab-chip--size):not(.fab-chip--clear-all)",
    );
    const clearAllButton = container.querySelector(".fab-chip--clear-all");

    const hasSizeChip = sizeChip && sizeChip.style.display !== "none";
    const hasRegularChips = regularChips.length > 0;

    // Show container if there's any chip
    if (hasSizeChip || hasRegularChips) {
      container.style.display = "flex";
      // Show Clear All if there are ANY active filters (regular chips OR size chip)
      if (clearAllButton) {
        clearAllButton.style.display =
          hasRegularChips || hasSizeChip ? "inline-flex" : "none";
      }
    } else {
      container.style.display = "none";
    }
  }

  /**
   * Calculates area in square feet from dimensions
   */
  calculateAreaFromDimensions(dimensions) {
    if (dimensions.shape === "Rectangular") {
      const widthTotal = dimensions.width.feet + dimensions.width.inches / 12;
      const lengthTotal =
        dimensions.length.feet + dimensions.length.inches / 12;
      return widthTotal * lengthTotal;
    } else if (dimensions.shape === "Round") {
      const diameterTotal =
        dimensions.diameter.feet + dimensions.diameter.inches / 12;
      const radius = diameterTotal / 2;
      return Math.PI * radius * radius;
    }
    return 0;
  }

  /**
   * Gets dimensions from size picker
   */
  getSizeDimensions(sizePicker) {
    // The shape input name is actually "shape-filter" (or "shape-filter-mobile")
    const shapeInput = sizePicker.querySelector(
      'input[name^="shape-filter"]:checked',
    );
    const shape = shapeInput?.value;
    debugLog("getSizeDimensions", "Shape detected", {
      shape,
      shapeInput: !!shapeInput,
    });

    if (shape === "Rectangular") {
      const widthFeet = parseInt(
        sizePicker.querySelector('[name="width-feet"]')?.value || 0,
      );
      const widthInches = parseInt(
        sizePicker.querySelector('[name="width-inches"]')?.value || 0,
      );
      const lengthFeet = parseInt(
        sizePicker.querySelector('[name="length-feet"]')?.value || 0,
      );
      const lengthInches = parseInt(
        sizePicker.querySelector('[name="length-inches"]')?.value || 0,
      );

      debugLog("getSizeDimensions", "Rectangular dimensions", {
        widthFeet,
        widthInches,
        lengthFeet,
        lengthInches,
      });

      // Only show chip if at least one dimension has a value
      if (
        widthFeet === 0 &&
        widthInches === 0 &&
        lengthFeet === 0 &&
        lengthInches === 0
      ) {
        debugLog("getSizeDimensions", "All dimensions are 0, returning null");
        return null;
      }

      return {
        shape: "Rectangular",
        width: { feet: widthFeet, inches: widthInches },
        length: { feet: lengthFeet, inches: lengthInches },
      };
    } else if (shape === "Round") {
      const diameterFeet = parseInt(
        sizePicker.querySelector('[name="diameter-feet"]')?.value || 0,
      );
      const diameterInches = parseInt(
        sizePicker.querySelector('[name="diameter-inches"]')?.value || 0,
      );

      debugLog("getSizeDimensions", "Round dimensions", {
        diameterFeet,
        diameterInches,
      });

      if (diameterFeet === 0 && diameterInches === 0) {
        debugLog("getSizeDimensions", "Diameter is 0, returning null");
        return null;
      }

      return {
        shape: "Round",
        diameter: { feet: diameterFeet, inches: diameterInches },
      };
    }

    return null;
  }

  /**
   * Formats size label based on dimensions
   * Examples:
   * - 8 x 5 sq ft (no inches)
   * - 12'8'' x 5'6'' sq ft (with inches)
   * - 12 ft round (no inches, round)
   * - 8'11'' ft round (with inches, round)
   */
  formatSizeLabel(dimensions) {
    if (dimensions.shape === "Rectangular") {
      const width = this.formatDimension(dimensions.width);
      const length = this.formatDimension(dimensions.length);
      return `${width} x ${length} sq ft`;
    } else if (dimensions.shape === "Round") {
      const diameter = this.formatDimension(dimensions.diameter);
      return `${diameter} ft round`;
    }
    return "";
  }

  /**
   * Formats a single dimension (feet + inches)
   * - If only feet: "8"
   * - If feet + inches: "12'8''"
   */
  formatDimension(dimension) {
    const { feet, inches } = dimension;

    if (inches > 0) {
      // Has inches, use foot and inch notation
      return `${feet}'${inches}''`;
    } else {
      // Only feet, just show the number
      return `${feet}`;
    }
  }

  /**
   * Updates all filter count indicators to reflect whether a custom size filter is active.
   * This handles:
   *  1. The Size row count badge in the drawer (data-size-count)
   *  2. The drawer header total count bubble (data-total-filter-count)
   *  3. The mobile toggle button total count badge (data-total-filter-count-toggle)
   * @param {boolean} sizeIsActive — true if a custom size selection is active
   */
  updateSizeFilterCounts(sizeIsActive) {
    debugLog("updateSizeFilterCounts", "Updating counts", { sizeIsActive });

    // Track previous state so we know whether to add or subtract
    const wasActive = !!this._sizeFilterCountedAsActive;
    this._sizeFilterCountedAsActive = sizeIsActive;

    // If state didn't change, nothing to do
    if (wasActive === sizeIsActive) {
      debugLog("updateSizeFilterCounts", "No state change, skipping");
      return;
    }

    const delta = sizeIsActive ? 1 : -1;

    // 1. Size row count badge in the drawer
    const drawer = this.mobileDrawer;
    if (drawer) {
      const sizeCountBadges = drawer.querySelectorAll("[data-size-count]");
      sizeCountBadges.forEach((badge) => {
        badge.style.display = sizeIsActive ? "" : "none";
      });
    }

    // 2. Drawer header total count bubble
    const drawerBubble = drawer
      ? drawer.querySelector("[data-total-filter-count]")
      : null;
    if (drawerBubble) {
      const currentCount = parseInt(drawerBubble.textContent.trim()) || 0;
      const newCount = Math.max(0, currentCount + delta);
      drawerBubble.textContent = newCount;
      drawerBubble.style.display = newCount > 0 ? "" : "none";
      debugLog("updateSizeFilterCounts", "Drawer header bubble", {
        currentCount,
        newCount,
      });
    }

    // 3. Mobile toggle button count badge
    const toggleBubble = document.querySelector(
      "[data-total-filter-count-toggle]",
    );
    if (toggleBubble) {
      const textEl = toggleBubble.querySelector(
        ".fab-filter-count-bubble__text",
      );
      if (textEl) {
        const currentCount = parseInt(textEl.textContent.trim()) || 0;
        const newCount = Math.max(0, currentCount + delta);
        textEl.textContent = newCount;
        toggleBubble.style.display = newCount > 0 ? "" : "none";
        debugLog("updateSizeFilterCounts", "Mobile toggle bubble", {
          currentCount,
          newCount,
        });
      }
    }
  }

  /**
   * Removes the size filter
   */
  removeSizeFilter() {
    debugLog("removeSizeFilter", "Size filter removal initiated", {
      timestamp: Date.now(),
    });

    // Only proceed if size picker exists (desktop or mobile drawer)
    const sizePicker = this.sizePicker;
    if (!sizePicker) {
      debugLog("removeSizeFilter", "Size picker not found, aborting");
      return;
    }

    // Clear localStorage
    localStorage.removeItem("fabricSizePickerState");
    debugLog("removeSizeFilter", "Cleared localStorage: fabricSizePickerState");

    // Reset all size picker selects to 0
    const sizeSelects = sizePicker.querySelectorAll("select");
    debugLog(
      "removeSizeFilter",
      `Found ${sizeSelects.length} size picker selects to reset`,
    );
    sizeSelects.forEach((select) => {
      debugLog(
        "removeSizeFilter",
        `Resetting select: ${select.name} from ${select.value} to 0`,
      );
      select.value = "0";
    });

    // Hide all size chips (desktop + mobile drawer)
    const desktopSizeChips = Array.from(
      this.querySelectorAll(".fab-chip--size"),
    );
    const drawer = this.mobileDrawer;
    const mobileSizeChips = drawer
      ? Array.from(drawer.querySelectorAll(".fab-chip--size"))
      : [];
    const allSizeChips = [...desktopSizeChips, ...mobileSizeChips];
    allSizeChips.forEach((chip) => {
      chip.style.display = "none";
    });
    debugLog("removeSizeFilter", `${allSizeChips.length} size chip(s) hidden`);

    // Update count badges (decrement totals, hide Size row badge)
    this.updateSizeFilterCounts(false);

    // Dispatch fabricSizeFilterCleared event to notify pricing script
    debugLog("removeSizeFilter", "Dispatching fabricSizeFilterCleared event");
    const event = new CustomEvent("fabricSizeFilterCleared", {
      detail: { source: "chipRemove" },
      bubbles: true,
    });
    document.dispatchEvent(event);

    // Trigger filter update
    const facetsForm = this.querySelector("facets-form-component");
    if (facetsForm) {
      debugLog("removeSizeFilter", "Calling facetsForm.updateFilters()");
      facetsForm.updateFilters();
    }
  }
}

if (!customElements.get("fabric-filters-component")) {
  customElements.define("fabric-filters-component", FabricFiltersComponent);
}
